import React, { memo, useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  PixelRatio,
  TouchableOpacity,
  StatusBar,
} from "react-native";
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import { useSelector } from "react-redux";

import t from "@constants/localization";
import Button from "@components/Button";
import testIds from "@constants/testIds";
import colors from "@constants/color";
import analytics from "@services/analytics";
import { MY_DELIVERY_EVENTS } from "@constants/analyticsEventsCT";
import { DrawerActions } from "@react-navigation/native";
import HamburgerIcon from "@assets/svg/Hamburger";
import AppText from "@components/AppText";
import metrics from "@constants/metrics";
import { variants } from "@utils/styles";
import RTOModal from "@components/DeliveryRTOOrder/RTOModal";
import DeliveryReturn from "./DeliveryReturn";
import PreviousDeliveryReturn from "./PreviousDeliveryReturn";
import UpcomingDeliveryReturn from "./UpcomingDeliveryReturn";

const Tab = createMaterialTopTabNavigator();

const PATH = "screens/MyDeliveries";

const { width } = Dimensions.get("window");
const scale = width / 360;

function normalize(size) {
  const newSize = size * scale;
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
}

const MyDeliveries = ({ navigation, route }) => {
  const [switchedBy, setSwitchedBy] = useState("swipe");
  const { currentRouteName = "" } = useSelector((state) => state.navigation);
  const cp_db_id = useSelector(({ auth }) => auth?.user?.cp_db_id);
  const openDrawer = () => {
    navigation.dispatch(DrawerActions.toggleDrawer());
  };

  const handleEvent = () => {
    if (["today", "upcoming", "past"].includes(currentRouteName)) {
      analytics.trackEvent(MY_DELIVERY_EVENTS.TAB_CLICKED, {
        screen: "My Delivery",
        tab: currentRouteName,
        type: switchedBy,
      });
    }
    setSwitchedBy("swipe");
  };

  const onOrdersPress = () => {
    analytics.trackEvent(MY_DELIVERY_EVENTS.ORDERS_CLICKED, {
      screen: "My Delivery",
    });
    navigation.navigate("OrdersList", {
      screen_name: "all orders",
    });
  };

  useEffect(() => {
    handleEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRouteName]);

  const Tabs = cp_db_id
    ? [
        {
          title: t(`${PATH}/today`),
          name: "today",
          screen: DeliveryReturn,
        },
      ]
    : [
        {
          title: t(`${PATH}/today`),
          name: "today",
          screen: DeliveryReturn,
        },
        {
          title: t(`${PATH}/upcoming`),
          name: "upcoming",
          screen: UpcomingDeliveryReturn,
        },
        {
          title: t(`${PATH}/previous`),
          name: "past",
          screen: PreviousDeliveryReturn,
        },
      ];

  const TabButton = memo(({ focused, i, tab }) => {
    return (
      <Text
        style={[
          styles.headerText,
          {
            color: focused ? colors.mediumBlack : colors.lightBlack,
            fontFamily: focused
              ? "NotoSansDevanagari-Bold"
              : "NotoSansDevanagari-Regular",
            width: width / 3,
          },
        ]}
      >
        {tab.title}
      </Text>
    );
  });

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" />
      <View style={styles.headerContainer}>
        <View style={styles.leftHeaderContainer}>
          <TouchableOpacity testID="mdi-menu" onPress={openDrawer}>
            <HamburgerIcon color={colors.black} />
          </TouchableOpacity>
          <AppText
            bold
            fontType="head1"
            style={[styles.textLeftPadding, variants.title_20]}
            ellipsizeMode="tail"
            numberOfLines={1}
          >
            {t(`${PATH}/title`)}
          </AppText>
        </View>
        {!cp_db_id && (
          <Button
            btnText={t(`${PATH}/allOrdersBtn`)}
            buttonColor={colors.white}
            textColor={colors.primary}
            onPress={onOrdersPress}
            buttonWidth={normalize(94)}
            testID={testIds.my_shop_all_order}
            borderRadius={8}
            borderColor={colors.primary}
            height={32}
            textStyle={styles.btnText}
          />
        )}
      </View>
      <Tab.Navigator
        backBehavior="none"
        screenOptions={() => ({
          tabBarActiveTintColor: "#ffffff",
          tabBarIndicatorStyle: { backgroundColor: colors.primary, height: 2 },
          tabBarContentContainerStyle: { paddingVertical: 4 },
          lazy: true,
        })}
      >
        {Tabs.map((tab, i) => {
          return (
            <Tab.Screen
              testID={`${i}-${testIds.tab}`}
              key={tab.name}
              options={{
                tabBarLabel: ({ focused }) => (
                  <TabButton focused={focused} i={i} tab={tab} />
                ),
              }}
              name={tab.name}
              component={tab.screen}
              listeners={() => ({
                tabPress: () => {
                  setSwitchedBy("click");
                },
              })}
            />
          );
        })}
      </Tab.Navigator>
      <RTOModal source="MyDelivery" />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  headerContainer: {
    paddingTop: 10,
    paddingBottom: 10,
    paddingHorizontal: metrics.mainContainerPadding,
    backgroundColor: "white",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  leftHeaderContainer: { flexDirection: "row", alignItems: "center" },
  headerText: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Regular",
    lineHeight: 24,
    textAlign: "center",
  },
  textLeftPadding: {
    paddingLeft: 10,
  },
  btnText: { fontSize: 14, lineHeight: 18 },
});

export default MyDeliveries;
